import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Phase2Card } from "@/components/portal/Phase2Card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, FunnelChart, Funnel, LabelList } from "recharts";

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const Route = createFileRoute("/admin/reports")({
  head: () => ({ meta: [{ title: "Reports — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Command Center" nav={ADMIN_NAV} tourKey="admin_tour">
        <AdminReports />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AdminReports() {
  const [production, setProduction] = useState<Array<{ agent: string; total: number }>>([]);
  const [conversion, setConversion] = useState<Array<{ source: string; leads: number; converted: number; rate: number }>>([]);
  const [funnel, setFunnel] = useState<Array<{ name: string; value: number; fill: string }>>([]);
  const [revenue, setRevenue] = useState<Array<{ product: string; total: number }>>([]);

  useEffect(() => { void load(); }, []);

  async function load() {
    const [pol, prof, leads, apps] = await Promise.all([
      supabase.from("policies").select("agent_id, product_type, face_amount, premium, status"),
      supabase.from("profiles").select("id, full_name"),
      supabase.from("leads").select("source, stage"),
      supabase.from("advisor_applications").select("status"),
    ]);

    const pm = new Map<string, string>();
    for (const p of (prof.data ?? []) as Array<{ id: string; full_name: string | null }>) pm.set(p.id, p.full_name || "Unnamed");

    // Production by agent
    const prodMap = new Map<string, number>();
    for (const p of (pol.data ?? []) as Array<{ agent_id: string | null; premium: number | null }>) {
      if (!p.agent_id) continue;
      prodMap.set(p.agent_id, (prodMap.get(p.agent_id) ?? 0) + (Number(p.premium) || 0));
    }
    setProduction(Array.from(prodMap.entries()).map(([id, total]) => ({ agent: pm.get(id) || "Strategist", total })).sort((a, b) => b.total - a.total).slice(0, 10));

    // Conversion by source
    const srcMap = new Map<string, { leads: number; converted: number }>();
    for (const l of (leads.data ?? []) as Array<{ source: string | null; stage: string }>) {
      const key = l.source || "Direct";
      const cur = srcMap.get(key) ?? { leads: 0, converted: 0 };
      cur.leads += 1; if (l.stage === "converted") cur.converted += 1;
      srcMap.set(key, cur);
    }
    setConversion(Array.from(srcMap.entries()).map(([source, v]) => ({ source, ...v, rate: v.leads ? (v.converted / v.leads) * 100 : 0 })));

    // Recruiting funnel
    const apsRaw = (apps.data ?? []) as Array<{ status: string }>;
    const stages = ["submitted", "screened", "interview", "approved", "onboarded"];
    const counts = stages.map(s => apsRaw.filter(a => a.status === s).length);
    const palette = ["#002D68", "#143D7C", "#A88B5C", "#C9A876", "#0E7C5A"];
    setFunnel(stages.map((name, i) => ({ name, value: counts[i] || 0, fill: palette[i] })));

    // Revenue by product
    const revMap = new Map<string, number>();
    for (const p of (pol.data ?? []) as Array<{ product_type: string | null; premium: number | null }>) {
      const key = p.product_type || "Other";
      revMap.set(key, (revMap.get(key) ?? 0) + (Number(p.premium) || 0));
    }
    setRevenue(Array.from(revMap.entries()).map(([product, total]) => ({ product, total })).sort((a, b) => b.total - a.total));
  }

  function exportCsv(name: string, rows: any[]) {
    if (!rows.length) return;
    const header = Object.keys(rows[0]);
    const lines = rows.map(r => header.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `${name}.csv`; a.click(); URL.revokeObjectURL(url);
  }

  return (
    <PortalPage eyebrow="Insights" title="Reports" intro="Production, conversion, recruiting, retention, and revenue analytics.">
      <Tabs defaultValue="production">
        <TabsList className="bg-cream-50">
          <TabsTrigger value="production">Production</TabsTrigger>
          <TabsTrigger value="conversion">Conversion</TabsTrigger>
          <TabsTrigger value="recruiting">Recruiting</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="retention">Retention</TabsTrigger>
        </TabsList>

        <TabsContent value="production" className="mt-6">
          <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-navy-800 text-[20px]">Production by Strategist (top 10)</h3><button onClick={() => exportCsv("production", production)} className="text-[11px] uppercase tracking-[0.12em] text-bronze-600">Export CSV</button></div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={production}>
                <CartesianGrid strokeDasharray="3 3" stroke="#A88B5C" opacity={0.2} />
                <XAxis dataKey="agent" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => fmt(Number(v))} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmt(Number(v))} />
                <Bar dataKey="total" fill="#002D68" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="conversion" className="mt-6">
          <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-navy-800 text-[20px]">Lead Conversion by Source</h3><button onClick={() => exportCsv("conversion", conversion)} className="text-[11px] uppercase tracking-[0.12em] text-bronze-600">Export CSV</button></div>
            <table className="w-full text-[13px]">
              <thead className="text-left text-charcoal-500 text-[10px] uppercase tracking-[0.12em]"><tr><th className="py-2">Source</th><th>Leads</th><th>Converted</th><th>Rate</th></tr></thead>
              <tbody>{conversion.map(c => <tr key={c.source} className="border-t border-bronze-600/10"><td className="py-2 text-navy-800">{c.source}</td><td>{c.leads}</td><td>{c.converted}</td><td>{c.rate.toFixed(1)}%</td></tr>)}</tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="recruiting" className="mt-6">
          <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6">
            <h3 className="font-display text-navy-800 text-[20px] mb-4">Recruiting Funnel</h3>
            <ResponsiveContainer width="100%" height={320}>
              <FunnelChart><Tooltip /><Funnel dataKey="value" data={funnel} isAnimationActive><LabelList position="right" dataKey="name" /></Funnel></FunnelChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="revenue" className="mt-6">
          <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-navy-800 text-[20px]">Revenue by Product</h3><button onClick={() => exportCsv("revenue", revenue)} className="text-[11px] uppercase tracking-[0.12em] text-bronze-600">Export CSV</button></div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#A88B5C" opacity={0.2} />
                <XAxis dataKey="product" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => fmt(Number(v))} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmt(Number(v))} />
                <Bar dataKey="total" fill="#A88B5C" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="retention" className="mt-6">
          <Phase2Card title="Retention by Cohort" message="Cohort retention requires longer policy history. The first cohort lands once we have 12 months of book data — until then we report production, conversion, recruiting, and revenue above." />
        </TabsContent>
      </Tabs>
    </PortalPage>
  );
}
